import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ArrowLeft,
  Mail,
  Globe,
  Users,
  DollarSign,
  FileText,
  CreditCard,
  Wallet,
  ExternalLink,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  TrendingUp,
  CheckCircle,
  Ban,
  Crown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatUSD, formatCount } from '../utils/formatAmount';
import { fetchWithAuth } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { useSetMerchantPlanMutation } from '../store/api/planApi';
import PageHeaderPro from '../components/common/PageHeaderPro';
import KpiStrip from '../components/common/KpiStrip';
import KpiCard from '../components/common/KpiCard';
import StatusBadge from '../components/common/StatusBadge';
import Skeleton, { TableSkeleton } from '../components/common/Skeleton';
import '../styles/enterprise.css';
import '../styles/responsive/pages/MerchantDetail.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// MER-02: preset suspension messages shown to the operator. The merchant sees
// the chosen text in their blocked popup and suspension email.
const SUSPEND_REASONS = [
  'Suspicious or fraudulent activity detected on your account.',
  'Compliance review required — please complete identity/KYC verification.',
  'Violation of MaxelPay’s Terms of Service.',
];

const MerchantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const { mode } = useTheme();

  const [merchant, setMerchant] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // FE-1: consolidated super-admin overview (payout/wallet/invoice/session aggregates).
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesPagination, setInvoicesPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPagination, setSessionsPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsPagination, setTransactionsPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const [copiedAddress, setCopiedAddress] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  // MER-02: suspend-reason modal (preset messages + optional manual reason).
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspendReasonKey, setSuspendReasonKey] = useState('');
  const [suspendCustomReason, setSuspendCustomReason] = useState('');

  // P-4/P-5: admin plan override.
  const [setMerchantPlan, { isLoading: planSaving }] = useSetMerchantPlanMutation();
  const [planData, setPlanData] = useState(null);
  const [planForm, setPlanForm] = useState({
    planKey: 'free',
    reason: '',
    durationDays: '',
    feePct: '',
    minGeneral: '',
    minEth: '',
    features: '',
  });

  const fetchMerchant = useCallback(async () => {
    if (!token || !id) return;

    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/merchants/${id}`);
      const data = await response.json();

      if (data.success) {
        setMerchant(data.data.merchant);
        setStats(data.data.stats);
      } else {
        toast.error(data.message || 'Failed to load merchant');
        navigate('/dashboard/merchants');
      }
    } catch (error) {
      console.error('Failed to fetch merchant:', error);
      toast.error('Failed to load merchant');
    } finally {
      setLoading(false);
    }
  }, [token, id, navigate]);

  // FE-1: BE-4 consolidated overview endpoint (payouts/wallets/invoices/sessions).
  const fetchOverview = useCallback(async () => {
    if (!token || !id) return;

    setOverviewLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/admin/merchants/${id}/overview`);
      const data = await response.json();
      if (data.success) {
        setOverview(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch merchant overview:', error);
    } finally {
      setOverviewLoading(false);
    }
  }, [token, id]);

  const fetchWallets = useCallback(async () => {
    if (!token || !id) return;

    setWalletsLoading(true);
    try {
      const isTestnet = mode === 'test';
      const response = await fetchWithAuth(`${API_BASE_URL}/wallets/merchant/${id}?isTestnet=${isTestnet}`);
      const data = await response.json();

      if (data.success) {
        setWallets(data.data.wallets || []);
      }
    } catch (error) {
      console.error('Failed to fetch wallets:', error);
    } finally {
      setWalletsLoading(false);
    }
  }, [token, id, mode]);

  const fetchInvoices = useCallback(async (page = 1) => {
    if (!token || !id) return;

    setInvoicesLoading(true);
    try {
      const isTestnet = mode === 'test';
      const params = new URLSearchParams({
        page,
        limit: invoicesPagination.limit,
        merchantId: id,
        isTestnet,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      });
      const response = await fetchWithAuth(`${API_BASE_URL}/invoices?${params}`);
      const data = await response.json();

      if (data.success) {
        setInvoices(data.data.invoices || []);
        setInvoicesPagination(prev => ({
          ...prev,
          page,
          total: data.data.pagination?.total || 0,
          totalPages: data.data.pagination?.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  }, [token, id, mode, invoicesPagination.limit]);

  const fetchSessions = useCallback(async (page = 1) => {
    if (!token || !id) return;

    setSessionsLoading(true);
    try {
      const isTestnet = mode === 'test';
      const params = new URLSearchParams({
        page,
        limit: sessionsPagination.limit,
        merchantId: id,
        isTestnet,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      });
      const response = await fetchWithAuth(`${API_BASE_URL}/v1/payments/sessions?${params}`);
      const data = await response.json();

      if (data.success) {
        setSessions(data.data.sessions || []);
        setSessionsPagination(prev => ({
          ...prev,
          page,
          total: data.data.pagination?.total || 0,
          totalPages: data.data.pagination?.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  }, [token, id, mode, sessionsPagination.limit]);

  const fetchTransactions = useCallback(async (page = 1) => {
    if (!token || !id) return;

    setTransactionsLoading(true);
    try {
      const isTestnet = mode === 'test';
      const params = new URLSearchParams({
        page,
        limit: transactionsPagination.limit,
        merchantId: id,
        isTestnet,
        sortBy: 'created_at',
        sortOrder: 'DESC',
      });
      const response = await fetchWithAuth(`${API_BASE_URL}/transactions?${params}`);
      const data = await response.json();

      if (data.success) {
        setTransactions(data.data.transactions || []);
        setTransactionsPagination(prev => ({
          ...prev,
          page,
          total: data.data.pagination?.total || 0,
          totalPages: data.data.pagination?.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  }, [token, id, mode, transactionsPagination.limit]);

  useEffect(() => {
    fetchMerchant();
    fetchOverview();
  }, [fetchMerchant, fetchOverview]);

  useEffect(() => {
    if (activeTab === 'wallets') {
      fetchWallets();
    } else if (activeTab === 'invoices') {
      fetchInvoices(1);
    } else if (activeTab === 'sessions') {
      fetchSessions(1);
    } else if (activeTab === 'transactions') {
      fetchTransactions(1);
    }
  }, [activeTab, fetchWallets, fetchInvoices, fetchSessions, fetchTransactions]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(text);
    setTimeout(() => setCopiedAddress(null), 2000);
    toast.success('Copied to clipboard');
  };

  const truncateAddress = (address) => {
    if (!address) return '-';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'succeeded':
      case 'completed':
      case 'overpaid':
        return 'status-success';
      case 'pending':
      case 'open':
      case 'awaiting_payment':
        return 'status-pending';
      case 'expired':
      case 'failed':
      case 'canceled':
      case 'cancelled':
        return 'status-failed';
      case 'partially_paid':
        return 'status-partial';
      default:
        return 'status-default';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Users },
    { id: 'wallets', label: 'Wallets', icon: Wallet },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'sessions', label: 'Payment Sessions', icon: CreditCard },
    { id: 'transactions', label: 'Transactions', icon: TrendingUp },
    { id: 'plan', label: 'Plan', icon: Crown },
  ];

  if (loading) {
    return (
      <div className="ent ent-page merchant-detail-page">
        <button className="back-btn" onClick={() => navigate('/dashboard/merchants')}>
          <ArrowLeft size={18} />
          Back to Merchants
        </button>

        <div className="md-detail-head-skeleton">
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="text" width="22%" />
        </div>

        <KpiStrip className="md-kpis">
          <KpiCard label="" value={<Skeleton variant="text" width="60%" height={24} />} />
          <KpiCard label="" value={<Skeleton variant="text" width="60%" height={24} />} />
          <KpiCard label="" value={<Skeleton variant="text" width="60%" height={24} />} />
          <KpiCard label="" value={<Skeleton variant="text" width="60%" height={24} />} />
        </KpiStrip>

        <div className="md-summary-grid">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ent-card md-summary-card">
              <div className="md-summary-head">
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="text" width="20%" />
              </div>
              <div className="md-summary-rows">
                <Skeleton variant="line" count={3} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="merchant-detail-page">
        <div className="error-container">
          <p>Merchant not found</p>
          <button className="btn-primary" onClick={() => navigate('/dashboard/merchants')}>
            Back to Merchants
          </button>
        </div>
      </div>
    );
  }

  // MER-02: suspend or reactivate this merchant (operator action, confirmed).
  const handleToggleSuspension = async () => {
    const suspended = merchant.status === 'suspended';
    if (suspended) {
      if (!window.confirm(`Reactivate ${merchant.email}? They will be able to use their account again.`)) return;
      submitSuspension('activate');
    } else {
      // Open the reason modal (preset messages + manual option).
      setSuspendReasonKey('');
      setSuspendCustomReason('');
      setSuspendModalOpen(true);
    }
  };

  const submitSuspension = async (action, reason) => {
    setStatusUpdating(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/merchants/${id}/${action}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: action === 'suspend' ? JSON.stringify({ reason }) : undefined,
      });
      const data = await response.json();
      if (data.success) {
        toast.success(action === 'activate' ? 'Merchant activated' : 'Merchant suspended');
        setSuspendModalOpen(false);
        fetchMerchant();
        fetchOverview();
      } else {
        toast.error(data.message || 'Action failed');
      }
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleConfirmSuspend = () => {
    const reason = suspendReasonKey === 'other' ? suspendCustomReason.trim() : suspendReasonKey;
    if (!reason) {
      toast.error('Please choose or enter a reason.');
      return;
    }
    submitSuspension('suspend', reason);
  };

  // P-4/P-5: submit the admin plan override.
  const handleSubmitPlan = async (e) => {
    e.preventDefault();
    const body = { id, planKey: planForm.planKey };

    if (planForm.planKey === 'premium') {
      // P-4: Premium override requires a reason and a duration.
      if (!planForm.reason.trim()) {
        toast.error('A reason is required for a Premium override');
        return;
      }
      const days = parseInt(planForm.durationDays, 10);
      if (!Number.isInteger(days) || days <= 0) {
        toast.error('A positive duration (days) is required for a Premium override');
        return;
      }
      body.reason = planForm.reason.trim();
      body.durationDays = days;
    }

    if (planForm.planKey === 'custom') {
      // P-5: assemble custom_overrides from the editor fields (only set values).
      const overrides = {};
      if (planForm.feePct !== '') overrides.fee_pct = Number(planForm.feePct);
      if (planForm.minGeneral !== '') overrides.min_fee_general_usd = Number(planForm.minGeneral);
      if (planForm.minEth !== '') overrides.min_fee_eth_usd = Number(planForm.minEth);
      const features = planForm.features
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);
      if (features.length) overrides.features = features;
      body.customOverrides = overrides;
    }

    try {
      const res = await setMerchantPlan(body).unwrap();
      setPlanData(res.data || null);
      toast.success(res.message || 'Merchant plan updated');
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to update plan');
    }
  };

  const ovMerchant = overview?.merchant;
  const ovPayouts = overview?.payouts;
  const ovWallets = overview?.wallets;
  const ovInvoices = overview?.invoices;
  const ovSessions = overview?.sessions;

  // Money/count display helpers — show "—" when the overview hasn't loaded yet.
  const usd = (v) => (overview ? `$${formatUSD(v)}` : '—');
  const cnt = (v) => (overview ? formatCount(v) : '—');

  const merchantName =
    ovMerchant?.name || `${merchant.firstName || ''} ${merchant.lastName || ''}`.trim() || merchant.email;

  return (
    <div className="ent ent-page merchant-detail-page">
      <button className="back-btn" onClick={() => navigate('/dashboard/merchants')}>
        <ArrowLeft size={18} />
        Back to Merchants
      </button>

      <PageHeaderPro
        title={merchantName}
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={overviewLoading ? 'Updating…' : merchant.email}
        actions={
          <>
            <span className="md-header-status">
              <StatusBadge status={merchant.status === 'suspended' ? 'failed' : 'active'} dot
                label={merchant.status === 'suspended' ? 'Suspended' : 'Active'} />
            </span>
            <button
              className="ent-btn ent-btn-secondary"
              type="button"
              onClick={() => navigate(`/dashboard/payout-requests?merchantId=${id}`)}
            >
              <Wallet size={16} />
              Payout queue
            </button>
            <button
              className={`ent-btn ${merchant.status === 'suspended' ? 'ent-btn-primary' : 'ent-btn-secondary'} md-suspend-btn`}
              onClick={handleToggleSuspension}
              disabled={statusUpdating}
              type="button"
            >
              {merchant.status === 'suspended' ? <CheckCircle size={16} /> : <Ban size={16} />}
              {statusUpdating ? 'Working…' : merchant.status === 'suspended' ? 'Activate' : 'Suspend'}
            </button>
          </>
        }
      />

      {/* FE-1: payout aggregate KPIs (consolidated overview endpoint) */}
      <KpiStrip className="md-kpis">
        <KpiCard
          label="PAYOUT PENDING"
          value={usd(ovPayouts?.pendingAmount)}
          sub={`${cnt(ovPayouts?.pendingCount)} pending`}
        />
        <KpiCard
          label="REQUESTED (RESERVED)"
          value={usd(ovPayouts?.requestedAmount)}
          sub="In-flight & held"
        />
        <KpiCard
          label="HELD"
          value={usd(ovPayouts?.heldAmount)}
          sub={`${cnt(ovPayouts?.heldCount)} held`}
        />
        <KpiCard
          label="LIFETIME PAID"
          value={usd(ovPayouts?.completedAmount)}
          sub={`${cnt(ovPayouts?.completedCount)} completed`}
        />
      </KpiStrip>

      {/* FE-1: wallets / invoices / sessions summary */}
      <div className="md-summary-grid">
        <div className="ent-card md-summary-card">
          <div className="md-summary-head">
            <span className="md-summary-title"><Wallet size={15} /> Wallets</span>
            <span className="md-summary-total">{cnt(ovWallets?.total)} total</span>
          </div>
          <div className="md-summary-rows">
            <div className="md-summary-row">
              <span>Available</span>
              <strong>{cnt(ovWallets?.available)}</strong>
            </div>
            <div className="md-summary-row">
              <span>Frozen</span>
              <strong>{cnt(ovWallets?.frozen)}</strong>
            </div>
            <div className="md-summary-row">
              <span>Sweeping</span>
              <strong>{cnt(ovWallets?.sweeping)}</strong>
            </div>
          </div>
        </div>

        <div className="ent-card md-summary-card">
          <div className="md-summary-head">
            <span className="md-summary-title"><FileText size={15} /> Invoices</span>
            <span className="md-summary-total">{cnt(ovInvoices?.total)} total</span>
          </div>
          <div className="md-summary-rows">
            <div className="md-summary-row">
              <span>Paid</span>
              <strong>{cnt(ovInvoices?.paid)}</strong>
            </div>
            <div className="md-summary-row">
              <span>Open</span>
              <strong>{cnt(ovInvoices?.open)}</strong>
            </div>
            <div className="md-summary-row">
              <span>Overdue</span>
              <strong>{cnt(ovInvoices?.overdue)}</strong>
            </div>
            <div className="md-summary-row md-summary-row--accent">
              <span>Outstanding</span>
              <strong>{usd(ovInvoices?.outstandingAmount)}</strong>
            </div>
          </div>
        </div>

        <div className="ent-card md-summary-card">
          <div className="md-summary-head">
            <span className="md-summary-title"><CreditCard size={15} /> Payment Sessions</span>
            <span className="md-summary-total">{cnt(ovSessions?.total)} total</span>
          </div>
          <div className="md-summary-rows">
            <div className="md-summary-row">
              <span>Paid</span>
              <strong>{cnt(ovSessions?.paid)}</strong>
            </div>
            <div className="md-summary-row">
              <span>Pending</span>
              <strong>{cnt(ovSessions?.pending)}</strong>
            </div>
            <div className="md-summary-row">
              <span>Plan</span>
              <strong style={{ textTransform: 'capitalize' }}>{ovMerchant?.planKey || '—'}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={16} />
              <span className="tab-label-text">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="detail-card">
              <h3>Business Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Business Name</label>
                  <span>{merchant.businessInfo?.businessName || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Business Type</label>
                  <span>{merchant.businessInfo?.businessType || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Website</label>
                  {merchant.businessInfo?.website ? (
                    /^https?:\/\//i.test(merchant.businessInfo.website) ? (
                      <a href={merchant.businessInfo.website} target="_blank" rel="noopener noreferrer">
                        {merchant.businessInfo.website}
                        <ExternalLink size={12} />
                      </a>
                    ) : (
                      <span>{merchant.businessInfo.website}</span>
                    )
                  ) : (
                    <span>-</span>
                  )}
                </div>
                <div className="info-item">
                  <label>Country</label>
                  <span>{merchant.businessInfo?.country || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Address</label>
                  <span>{merchant.businessInfo?.address || '-'}</span>
                </div>
                <div className="info-item">
                  <label>Phone</label>
                  <span>{merchant.businessInfo?.phone || '-'}</span>
                </div>
              </div>
            </div>

            <div className="detail-card">
              <h3>Account Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Email</label>
                  <span>{merchant.email}</span>
                </div>
                <div className="info-item">
                  <label>Account Created</label>
                  <span>{formatDateTime(merchant.createdAt)}</span>
                </div>
                <div className="info-item">
                  <label>Last Login</label>
                  <span>{formatDateTime(merchant.lastLogin)}</span>
                </div>
                <div className="info-item">
                  <label>Verification Status</label>
                  <span className={`status-badge ${merchant.isVerified ? 'status-verified' : 'status-unverified'}`}>
                    {merchant.isVerified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
                {merchant.businessInfo?.ethereumAddress && (
                  <div className="info-item full-width">
                    <label>Payout Address</label>
                    <div className="address-display">
                      <span className="address-text">{merchant.businessInfo.ethereumAddress}</span>
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(merchant.businessInfo.ethereumAddress)}
                      >
                        {copiedAddress === merchant.businessInfo.ethereumAddress ? (
                          <Check size={14} />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'wallets' && (
          <div className="wallets-section">
            <div className="section-header">
              <h3>Merchant Wallets </h3>
              <button className="btn-icon" onClick={fetchWallets}>
                <RefreshCw size={16} />
              </button>
            </div>
            {walletsLoading ? (
              <div className="data-table-container">
                <TableSkeleton rows={5} cols={3} />
              </div>
            ) : wallets.length === 0 ? (
              <div className="empty-state">
                <Wallet size={48} />
                <p>No wallets found</p>
                <span>Wallets will appear here when assigned to payment sessions</span>
              </div>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Wallet Address</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wallets.map((wallet) => (

                      <tr key={wallet.id}>
                        <td>
                          <div className="wallet-address-cell">
                            <span className="address-text">{wallet.wallet_address}</span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${wallet.status === 'available'
                                ? 'status-success'
                                : 'status-pending'
                              }`}
                          >
                            {wallet.status === 'frozen' && 'In Use'}
                            {wallet.status === 'sweeping' && 'Swap'}
                            {wallet.status === 'available' && 'Available'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="copy-btn-table"
                            onClick={() => copyToClipboard(wallet.wallet_address)}
                            title="Copy to clipboard"
                          >
                            {copiedAddress === wallet.wallet_address ? <Check size={16} /> : <Copy size={16} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Mobile card view — shown via CSS at <=576px */}
                <div className="md-card-list">
                  {wallets.map((wallet) => (
                    <div key={wallet.id} className="md-row-card">
                      <div className="md-card-top">
                        <span className="md-card-primary mono">{wallet.wallet_address}</span>
                        <span
                          className={`status-badge ${wallet.status === 'available' ? 'status-success' : 'status-pending'}`}
                        >
                          {wallet.status === 'frozen' && 'In Use'}
                          {wallet.status === 'sweeping' && 'Swap'}
                          {wallet.status === 'available' && 'Available'}
                        </span>
                      </div>
                      <div className="md-card-row">
                        <span className="md-card-label">Actions</span>
                        <button
                          className="copy-btn-table"
                          onClick={() => copyToClipboard(wallet.wallet_address)}
                          title="Copy to clipboard"
                        >
                          {copiedAddress === wallet.wallet_address ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="data-section">
            <div className="section-header">
              <h3>Invoices ({invoicesPagination.total})</h3>
              <button className="btn-icon" onClick={() => fetchInvoices(invoicesPagination.page)}>
                <RefreshCw size={16} />
              </button>
            </div>
            {invoicesLoading ? (
              <div className="data-table-container">
                <TableSkeleton rows={5} cols={6} />
              </div>
            ) : invoices.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <p>No invoices found</p>
              </div>
            ) : (
              <>
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice ID</th>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="invoice-id">{invoice.invoiceId}</td>
                          <td>
                            <div className="customer-info">
                              <span className="customer-name">{invoice.customerName || '-'}</span>
                              <span className="customer-email">{invoice.customerEmail}</span>
                            </div>
                          </td>
                          <td className="amount">${formatUSD(invoice.amount)}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td>{formatDate(invoice.createdAt)}</td>
                          <td>{formatDate(invoice.dueDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile card view — shown via CSS at <=576px */}
                  <div className="md-card-list">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="md-row-card">
                        <div className="md-card-top">
                          <span className="md-card-primary mono">{invoice.invoiceId}</span>
                          <span className={`status-badge ${getStatusBadgeClass(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Customer</span>
                          <span className="md-card-value">{invoice.customerName || invoice.customerEmail || '-'}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Amount</span>
                          <span className="md-card-value">${formatUSD(invoice.amount)}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Created</span>
                          <span className="md-card-value">{formatDate(invoice.createdAt)}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Due</span>
                          <span className="md-card-value">{formatDate(invoice.dueDate)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {invoicesPagination.totalPages > 1 && (
                  <Pagination
                    pagination={invoicesPagination}
                    onPageChange={(page) => fetchInvoices(page)}
                  />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="data-section">
            <div className="section-header">
              <h3>Payment Sessions ({sessionsPagination.total})</h3>
              <button className="btn-icon" onClick={() => fetchSessions(sessionsPagination.page)}>
                <RefreshCw size={16} />
              </button>
            </div>
            {sessionsLoading ? (
              <div className="data-table-container">
                <TableSkeleton rows={5} cols={5} />
              </div>
            ) : sessions.length === 0 ? (
              <div className="empty-state">
                <CreditCard size={48} />
                <p>No payment sessions found</p>
              </div>
            ) : (
              <>
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Session ID</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.map((session) => (
                        <tr key={session.id}>
                          <td className="session-id">{truncateAddress(session.sessionId)}</td>
                          <td className="amount">
                            ${formatUSD(session.amount)} {session.currency}
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(session.status)}`}>
                              {session.status}
                            </span>
                          </td>
                          <td>{formatDateTime(session.createdAt)}</td>
                          <td>{formatDateTime(session.expiresAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile card view — shown via CSS at <=576px */}
                  <div className="md-card-list">
                    {sessions.map((session) => (
                      <div key={session.id} className="md-row-card">
                        <div className="md-card-top">
                          <span className="md-card-primary mono">{truncateAddress(session.sessionId)}</span>
                          <span className={`status-badge ${getStatusBadgeClass(session.status)}`}>
                            {session.status}
                          </span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Amount</span>
                          <span className="md-card-value">${formatUSD(session.amount)} {session.currency}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Created</span>
                          <span className="md-card-value">{formatDateTime(session.createdAt)}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Expires</span>
                          <span className="md-card-value">{formatDateTime(session.expiresAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {sessionsPagination.totalPages > 1 && (
                  <Pagination
                    pagination={sessionsPagination}
                    onPageChange={(page) => fetchSessions(page)}
                  />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="data-section">
            <div className="section-header">
              <h3>Transactions ({transactionsPagination.total})</h3>
              <button className="btn-icon" onClick={() => fetchTransactions(transactionsPagination.page)}>
                <RefreshCw size={16} />
              </button>
            </div>
            {transactionsLoading ? (
              <div className="data-table-container">
                <TableSkeleton rows={5} cols={6} />
              </div>
            ) : transactions.length === 0 ? (
              <div className="empty-state">
                <TrendingUp size={48} />
                <p>No transactions found</p>
              </div>
            ) : (
              <>
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>TX Hash</th>
                        <th>Network</th>
                        <th>Amount</th>
                        <th>USD Value</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td className="tx-hash">
                            {tx.txHash ? truncateAddress(tx.txHash) : '-'}
                          </td>
                          <td>{tx.network || '-'}</td>
                          <td className="amount">
                            {tx.amount} {tx.currency}
                          </td>
                          <td className="amount">${formatUSD(tx.amountUsd || tx.amount)}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(tx.status)}`}>
                              {tx.status}
                            </span>
                          </td>
                          <td>{formatDateTime(tx.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Mobile card view — shown via CSS at <=576px */}
                  <div className="md-card-list">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="md-row-card">
                        <div className="md-card-top">
                          <span className="md-card-primary mono">{tx.txHash ? truncateAddress(tx.txHash) : '-'}</span>
                          <span className={`status-badge ${getStatusBadgeClass(tx.status)}`}>
                            {tx.status}
                          </span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Network</span>
                          <span className="md-card-value">{tx.network || '-'}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Amount</span>
                          <span className="md-card-value">{tx.amount} {tx.currency}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">USD Value</span>
                          <span className="md-card-value">${formatUSD(tx.amountUsd || tx.amount)}</span>
                        </div>
                        <div className="md-card-row">
                          <span className="md-card-label">Date</span>
                          <span className="md-card-value">{formatDateTime(tx.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {transactionsPagination.totalPages > 1 && (
                  <Pagination
                    pagination={transactionsPagination}
                    onPageChange={(page) => fetchTransactions(page)}
                  />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'plan' && (
          <div className="overview-grid">
            <div className="detail-card">
              <h3>Current Plan</h3>
              {planData ? (
                <div className="info-grid">
                  <div className="info-item">
                    <label>Plan</label>
                    <span style={{ textTransform: 'capitalize' }}>{planData.planKey}</span>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <span className={`status-badge ${getStatusBadgeClass(planData.status)}`}>
                      {planData.status}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Transaction Fee</label>
                    <span>{planData.feePct != null ? `${planData.feePct}%` : '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Min Fee (general)</label>
                    <span>{planData.minimums?.general != null ? `$${formatUSD(planData.minimums.general)}` : '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Min Fee (ETH)</label>
                    <span>{planData.minimums?.eth != null ? `$${formatUSD(planData.minimums.eth)}` : '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Grandfathered Until</label>
                    <span>{planData.grandfatheredUntil ? formatDate(planData.grandfatheredUntil) : '—'}</span>
                  </div>
                </div>
              ) : (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  Set a plan below to view and override this merchant's effective plan.
                </p>
              )}
            </div>

            <div className="detail-card">
              <h3>Override Plan</h3>
              <form className="plan-override-form" onSubmit={handleSubmitPlan}>
                <div className="info-item">
                  <label>Plan</label>
                  <select
                    value={planForm.planKey}
                    onChange={(e) => setPlanForm((p) => ({ ...p, planKey: e.target.value }))}
                  >
                    <option value="free">Free</option>
                    <option value="premium">Premium</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {planForm.planKey === 'premium' && (
                  <>
                    <div className="info-item">
                      <label>Reason (required)</label>
                      <input
                        type="text"
                        value={planForm.reason}
                        onChange={(e) => setPlanForm((p) => ({ ...p, reason: e.target.value }))}
                        placeholder="Why is this merchant being upgraded?"
                      />
                    </div>
                    <div className="info-item">
                      <label>Duration (days, required)</label>
                      <input
                        type="number"
                        min="1"
                        value={planForm.durationDays}
                        onChange={(e) => setPlanForm((p) => ({ ...p, durationDays: e.target.value }))}
                        placeholder="e.g. 90"
                      />
                    </div>
                  </>
                )}

                {planForm.planKey === 'custom' && (
                  <>
                    <div className="info-item">
                      <label>Fee %</label>
                      <input
                        type="number"
                        step="0.1"
                        value={planForm.feePct}
                        onChange={(e) => setPlanForm((p) => ({ ...p, feePct: e.target.value }))}
                        placeholder="e.g. 1.5"
                      />
                    </div>
                    <div className="info-item">
                      <label>Min Fee — general (USD)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.minGeneral}
                        onChange={(e) => setPlanForm((p) => ({ ...p, minGeneral: e.target.value }))}
                        placeholder="e.g. 0.75"
                      />
                    </div>
                    <div className="info-item">
                      <label>Min Fee — ETH (USD)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={planForm.minEth}
                        onChange={(e) => setPlanForm((p) => ({ ...p, minEth: e.target.value }))}
                        placeholder="e.g. 1.50"
                      />
                    </div>
                    <div className="info-item">
                      <label>Features (comma-separated keys)</label>
                      <input
                        type="text"
                        value={planForm.features}
                        onChange={(e) => setPlanForm((p) => ({ ...p, features: e.target.value }))}
                        placeholder="e.g. analytics, priority_payouts"
                      />
                    </div>
                  </>
                )}

                <button type="submit" className="btn-primary" disabled={planSaving}>
                  {planSaving ? 'Saving...' : 'Apply Plan'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {suspendModalOpen && (
        <div className="md-suspend-overlay" role="dialog" aria-modal="true" aria-labelledby="md-suspend-title">
          <div className="md-suspend-modal">
            <div className="md-suspend-head">
              <span className="md-suspend-ic"><Ban size={20} /></span>
              <h3 id="md-suspend-title">Suspend {merchant?.email}</h3>
            </div>
            <p className="md-suspend-sub">
              The merchant can still sign in, but their dashboard and payments will be blocked.
              They’ll see this reason and get an email at {merchant?.email}.
            </p>
            <div className="md-suspend-reasons">
              {SUSPEND_REASONS.map((r) => (
                <label key={r} className={`md-suspend-opt ${suspendReasonKey === r ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="suspendReason"
                    checked={suspendReasonKey === r}
                    onChange={() => setSuspendReasonKey(r)}
                  />
                  <span>{r}</span>
                </label>
              ))}
              <label className={`md-suspend-opt ${suspendReasonKey === 'other' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="suspendReason"
                  checked={suspendReasonKey === 'other'}
                  onChange={() => setSuspendReasonKey('other')}
                />
                <span>Other (write a custom message)</span>
              </label>
              {suspendReasonKey === 'other' && (
                <textarea
                  className="md-suspend-textarea"
                  rows={3}
                  placeholder="Enter the reason shown to the merchant…"
                  value={suspendCustomReason}
                  onChange={(e) => setSuspendCustomReason(e.target.value)}
                  autoFocus
                />
              )}
            </div>
            <div className="md-suspend-actions">
              <button className="ent-btn ent-btn-secondary" onClick={() => setSuspendModalOpen(false)} disabled={statusUpdating}>
                Cancel
              </button>
              <button className="ent-btn ent-btn-danger" onClick={handleConfirmSuspend} disabled={statusUpdating}>
                {statusUpdating ? 'Suspending…' : 'Suspend merchant'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* MER-02 suspend modal */
        .md-suspend-overlay {
          position: fixed; inset: 0; z-index: 1000;
          display: flex; align-items: center; justify-content: center; padding: 16px;
          background: rgba(15,18,34,0.6);
        }
        .md-suspend-modal {
          width: 100%; max-width: 460px;
          background: var(--surface, #fff); border-radius: 14px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.3); padding: 22px;
        }
        .md-suspend-head { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
        .md-suspend-ic {
          width: 36px; height: 36px; flex-shrink: 0; display: grid; place-items: center;
          border-radius: 9px; background: #fee2e2; color: #dc2626;
        }
        .md-suspend-head h3 { margin: 0; font-size: 16px; font-weight: 700; color: var(--ink, #16182e); word-break: break-all; }
        .md-suspend-sub { margin: 0 0 16px; font-size: 13px; color: var(--t-500, #475569); line-height: 1.5; }
        .md-suspend-reasons { display: flex; flex-direction: column; gap: 8px; margin-bottom: 18px; }
        .md-suspend-opt {
          display: flex; align-items: flex-start; gap: 10px; cursor: pointer;
          padding: 10px 12px; border: 1px solid var(--line, #e2e8f0); border-radius: 10px;
          font-size: 13px; color: var(--ink, #16182e); min-height: 44px;
        }
        .md-suspend-opt.active { border-color: var(--brand-600, #6366f1); background: var(--brand-100, #eef2ff); }
        .md-suspend-opt input { margin-top: 2px; }
        .md-suspend-textarea {
          width: 100%; border: 1px solid var(--line, #e2e8f0); border-radius: 10px;
          padding: 10px 12px; font: inherit; font-size: 13px; resize: vertical; box-sizing: border-box;
        }
        .md-suspend-actions { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
        .md-suspend-actions .ent-btn { min-height: 44px; }
        .ent-btn-danger { background: #dc2626; color: #fff; border: 1px solid #dc2626; }
        .ent-btn-danger:hover:not(:disabled) { background: #b91c1c; }

        /* FE-1 enterprise overview blocks */
        .merchant-detail-page .md-header-status { display: inline-flex; align-items: center; }

        .merchant-detail-page .md-detail-head-skeleton {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .merchant-detail-page .md-kpis { margin-bottom: 1.5rem; }

        .merchant-detail-page .md-summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .merchant-detail-page .md-summary-card {
          border: 1px solid var(--ent-border);
          border-radius: var(--ent-radius);
          background: var(--ent-card);
          padding: 1.25rem;
          box-shadow: var(--ent-shadow);
        }

        .merchant-detail-page .md-summary-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .merchant-detail-page .md-summary-title {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--ent-text);
        }

        .merchant-detail-page .md-summary-total {
          font-size: 0.78rem;
          color: var(--ent-muted);
        }

        .merchant-detail-page .md-summary-rows {
          display: flex;
          flex-direction: column;
          gap: 0.625rem;
        }

        .merchant-detail-page .md-summary-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.85rem;
          color: var(--ent-text-2);
        }

        .merchant-detail-page .md-summary-row strong {
          color: var(--ent-text);
          font-weight: 600;
        }

        .merchant-detail-page .md-summary-row--accent {
          padding-top: 0.625rem;
          border-top: 1px solid var(--ent-border);
        }

        .merchant-detail-page .md-summary-row--accent strong { color: var(--ent-amber); }

        .merchant-detail-page {
          padding: 0;
        }

        .page-header {
          margin-bottom: 1.5rem;
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: transparent;
          border: none;
          color: var(--ent-text-2);
          font-size: 0.85rem;
          cursor: pointer;
          padding: 0.25rem 0 0.75rem;
        }

        .back-btn:hover {
          color: var(--ent-text);
        }

        /* Merchant Header Card */
        .merchant-header-card {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.5rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          margin-bottom: 1.5rem;
        }

        .merchant-header-left {
          display: flex;
          gap: 1.25rem;
        }

        .merchant-avatar-xl {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1.5rem;
          flex-shrink: 0;
        }

        .merchant-header-info h1 {
          margin: 0 0 0.25rem 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .merchant-email {
          color: var(--text-secondary);
          font-size: 0.9rem;
          margin: 0 0 0.75rem 0;
        }

        .merchant-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .business-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: var(--bg-secondary);
          border-radius: 8px;
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        .status-suspended {
          background: rgba(220, 38, 38, 0.12);
          color: #b91c1c;
        }

        .status-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.5rem 0.9rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
        }

        .status-action-btn:disabled { opacity: 0.6; cursor: default; }

        .status-action-btn.suspend {
          background: rgba(220, 38, 38, 0.1);
          color: #b91c1c;
          border-color: rgba(220, 38, 38, 0.3);
        }

        .status-action-btn.activate {
          background: rgba(16, 185, 129, 0.1);
          color: #047857;
          border-color: rgba(16, 185, 129, 0.3);
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.25rem;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
        }

        .stat-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          border-radius: 12px;
          color: white;
          flex-shrink: 0;
        }

        .stat-content {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .stat-label {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        /* Tabs */
        .tabs-container {
          margin-bottom: 1.5rem;
          border-bottom: 1px solid var(--border-color);
        }

        .tabs {
          display: flex;
          gap: 0.5rem;
          overflow-x: auto;
        }

        .tab {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 1.25rem;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-secondary);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          margin-bottom: -1px;
        }

        .tab:hover {
          color: var(--text-primary);
        }

        .tab.active {
          color: var(--accent-primary);
          border-bottom-color: var(--accent-primary);
        }

        /* Tab Content */
        .tab-content {
          min-height: 400px;
        }

        /* Overview Grid */
        .overview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1.5rem;
        }

        .detail-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 1.5rem;
        }

        .detail-card h3 {
          margin: 0 0 1.25rem 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-item.full-width {
          grid-column: span 2;
        }

        .info-item label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .info-item span {
          font-size: 0.9rem;
          color: var(--text-primary);
        }

        .info-item a {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          color: var(--accent-primary);
          text-decoration: none;
          font-size: 0.9rem;
        }

        .info-item a:hover {
          text-decoration: underline;
        }

        .address-display {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--bg-secondary);
          border-radius: 6px;
        }

        .address-text {
          font-family: var(--font-mono);
          font-size: 0.85rem;
          color: var(--text-primary);
          word-break: break-all;
        }

        .copy-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .copy-btn:hover {
          color: var(--accent-primary);
        }

        /* Wallets Section */
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .section-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .btn-icon {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 0.5rem;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .btn-icon:hover {
          color: var(--accent-primary);
          border-color: var(--accent-primary);
        }

        .wallet-address-cell {
          font-family: var(--font-mono);
          font-size: 0.85rem;
        }

        .wallet-address-cell .address-text {
          color: var(--text-primary);
          word-break: break-all;
        }

        .copy-btn-table {
          background: transparent;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          padding: 0.5rem;
          color: var(--text-muted);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .copy-btn-table:hover {
          color: var(--accent-primary);
          border-color: var(--accent-primary);
          background: var(--bg-secondary);
        }

        /* Data Tables */
        .data-section {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 1.5rem;
        }

        .data-table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--border-color);
          background: var(--bg-secondary);
        }

        .data-table td {
          padding: 0.875rem 1rem;
          border-bottom: 1px solid var(--border-color);
          font-size: 0.875rem;
          color: var(--text-primary);
        }

        .data-table tbody tr:last-child td {
          border-bottom: none;
        }

        .data-table tbody tr:hover {
          background: var(--bg-secondary);
        }

        .invoice-id, .session-id, .tx-hash {
          font-family: var(--font-mono);
          font-size: 0.8rem;
        }

        .customer-info {
          display: flex;
          flex-direction: column;
        }

        .customer-name {
          font-weight: 500;
        }

        .customer-email {
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .amount {
          font-weight: 500;
        }

        /* Status Badges */
        .status-badge {
          display: inline-flex;
          padding: 0.25rem 0.625rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-verified, .status-success {
          background: var(--ok-soft);
          color: var(--ok);
        }

        .status-unverified, .status-pending {
          background: var(--warn-soft);
          color: var(--warn);
        }

        .status-failed {
          background: var(--err-soft);
          color: var(--err);
        }

        .status-partial {
          background: var(--accent-soft);
          color: var(--accent-ink);
        }

        .status-default {
          background: var(--bg-inset);
          color: var(--ink-secondary);
        }

        /* Empty States */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: var(--text-secondary);
        }

        .empty-state svg {
          color: var(--ink-muted);
          margin-bottom: 0.75rem;
        }

        .empty-state p {
          margin: 0 0 0.25rem 0;
          font-weight: 500;
          color: var(--text-primary);
        }

        .empty-state span {
          font-size: 0.875rem;
        }

        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          gap: 1rem;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: var(--primary);
          border: none;
          border-radius: var(--r-sm);
          color: white;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
        }

        .btn-primary:hover {
          background: var(--primary-hover);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Plan override form */
        .plan-override-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .plan-override-form select,
        .plan-override-form input {
          padding: 0.625rem 0.75rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-primary);
          font-size: 0.875rem;
          width: 100%;
        }

        .plan-override-form select:focus,
        .plan-override-form input:focus {
          outline: none;
          border-color: var(--accent-primary);
        }

        .plan-override-form .btn-primary {
          align-self: flex-start;
          min-height: 44px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .merchant-header-card {
            flex-direction: column;
            gap: 1rem;
          }

          .merchant-header-left {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .merchant-meta {
            justify-content: center;
          }

          .overview-grid {
            grid-template-columns: 1fr;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .info-item.full-width {
            grid-column: span 1;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </div>
  );
};

const Pagination = ({ pagination, onPageChange }) => {
  const { page, totalPages, total, limit } = pagination;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        Showing {startItem} to {endItem} of {total}
      </div>
      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="page-info">
          Page {page} of {totalPages}
        </span>
        <button
          className="pagination-btn"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <style>{`
        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 0;
          margin-top: 1rem;
          border-top: 1px solid var(--border-color);
        }

        .pagination-info {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pagination-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .pagination-btn:hover:not(:disabled) {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }

        .pagination-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .page-info {
          font-size: 0.875rem;
          color: var(--text-secondary);
          padding: 0 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default MerchantDetail;
